/**
 * @fileoverview "Lesson markdown" — the deliberately tiny formatting subset that
 * lesson summaries and instructor notes are written in.
 *
 * WHY A SUBSET RATHER THAN A RICH-TEXT EDITOR: a lesson summary is rendered by
 * five different engines — this app, the student portal, an HTML email in
 * whatever client a parent uses, html2pdf on web, and expo-print on mobile.
 * Storing real HTML would mean sanitising it on the way in and re-styling it
 * per engine; storing plain text with a handful of markers keeps the stored
 * value human-readable, diffable, and safe to escape at every render site.
 *
 * THE SUBSET (and nothing else):
 *   - `**bold**`, `*italic*` and `++underline++` (underline wraps the others:
 *     `++**both**++`)
 *   - lines starting `- `, `* ` or `• ` are a bulleted list
 *   - lines starting `1. ` / `1) ` are a numbered list
 *   - a blank line starts a new paragraph; a single newline is a line break
 *
 * Plain prose parses to one paragraph whose newlines survive, which is the
 * whole point: text typed with dashes as bullets used to be flattened into a
 * run-on paragraph by every HTML surface (an unstyled `<td>`/`<p>` collapses
 * newlines), so what a parent received never matched what the instructor typed.
 *
 * Headings, links, images, tables and code are intentionally absent. Every
 * marker here is one a teacher already types by hand; anything more would need
 * an editor to discover and a sanitiser to trust.
 *
 * Mirrored by `backend/services/rich_text.py` (summary emails) and
 * `mobile/src/utils/richText.js` (the iOS app). The three implementations must
 * agree — their test suites assert the same fixtures.
 */

/** Lines that open a bulleted item: "- x", "* x", "• x". */
const BULLET_RE = /^[ \t]*[-*•][ \t]+(.*)$/;
/** Lines that open a numbered item: "1. x", "2) x". */
const ORDERED_RE = /^[ \t]*(\d{1,3})[.)][ \t]+(.*)$/;

/** `++underline++`. Read before bold and italic, which it may contain. */
const UNDERLINE_RE = /\+\+(.+?)\+\+/g;

/** Bold and italic runs of a stretch of text with no underline markers. */
function parseEmphasis(text, underline) {
  const runs = [];
  const mark = (run) => (underline ? { ...run, underline: true } : run);
  // Longest marker first: `***x***` must not be read as `**` + `*x*`, which
  // left a stray asterisk visible to families the moment an instructor
  // applied bold AND italic to the same words.
  const re = /\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*/g;
  let last = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) runs.push(mark({ type: "text", value: text.slice(last, match.index) }));
    if (match[1] !== undefined) runs.push(mark({ type: "bolditalic", value: match[1] }));
    else if (match[2] !== undefined) runs.push(mark({ type: "bold", value: match[2] }));
    else runs.push(mark({ type: "italic", value: match[3] }));
    last = match.index + match[0].length;
  }
  if (last < text.length) runs.push(mark({ type: "text", value: text.slice(last) }));
  return runs;
}

/**
 * Split one line into inline runs.
 *
 * `++underline++` is read first and its inside parsed like any other text, so
 * underlined words can also be bold or italic. `**bold**` is matched before
 * `*italic*` so the greedy-star ambiguity resolves the way the toolbar writes
 * it. An unmatched marker is literal text — a lone asterisk in "5 * 4" must
 * never eat the rest of the line.
 *
 * A run carries `underline: true` only when it is underlined, so every run
 * written before underline existed reads exactly as it did.
 *
 * @param {string} line
 * @returns {Array<{type: 'text'|'bold'|'italic'|'bolditalic', value: string, underline?: true}>}
 */
export function parseInline(line) {
  const runs = [];
  let last = 0;
  let match;
  UNDERLINE_RE.lastIndex = 0;
  while ((match = UNDERLINE_RE.exec(line)) !== null) {
    if (match.index > last) runs.push(...parseEmphasis(line.slice(last, match.index), false));
    runs.push(...parseEmphasis(match[1], true));
    last = match.index + match[0].length;
  }
  if (last < line.length) runs.push(...parseEmphasis(line.slice(last), false));
  // An empty line still needs one run so callers can render a <br> for it.
  return runs.length > 0 ? runs : [{ type: "text", value: "" }];
}

/**
 * Parse lesson markdown into renderable blocks.
 *
 * @param {string} text
 * @returns {Array<{type: 'paragraph'|'bullet'|'ordered', lines: Array<Array<Object>>, start?: number}>}
 *   `lines` holds one inline-run array per line (paragraph) or per item (list).
 */
export function parseRichText(text) {
  const source = String(text ?? "").replace(/\r\n?/g, "\n");
  if (!source.trim()) return [];

  const blocks = [];
  let current = null;

  const flush = () => {
    if (current && current.lines.length > 0) blocks.push(current);
    current = null;
  };

  for (const raw of source.split("\n")) {
    if (!raw.trim()) {
      // A blank line ends whatever block was open. Consecutive blanks collapse
      // rather than emitting empty paragraphs.
      flush();
      continue;
    }

    const bullet = raw.match(BULLET_RE);
    if (bullet) {
      if (current?.type !== "bullet") flush();
      current = current?.type === "bullet" ? current : { type: "bullet", lines: [] };
      current.lines.push(parseInline(bullet[1]));
      continue;
    }

    const ordered = raw.match(ORDERED_RE);
    if (ordered) {
      if (current?.type !== "ordered") flush();
      current =
        current?.type === "ordered"
          ? current
          : { type: "ordered", lines: [], start: Number(ordered[1]) || 1 };
      current.lines.push(parseInline(ordered[2]));
      continue;
    }

    if (current?.type !== "paragraph") flush();
    current = current?.type === "paragraph" ? current : { type: "paragraph", lines: [] };
    current.lines.push(parseInline(raw.trim()));
  }
  flush();
  return blocks;
}

const escapeHtml = (str) =>
  String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const emphasisHtml = (run) => {
  const value = escapeHtml(run.value);
  if (run.type === "bolditalic") return `<strong><em>${value}</em></strong>`;
  if (run.type === "bold") return `<strong>${value}</strong>`;
  if (run.type === "italic") return `<em>${value}</em>`;
  return value;
};

/** Neighbouring underlined runs share one `<u>`, the shape the editor reads back. */
const inlineToHtml = (runs) => {
  let out = "";
  for (let i = 0; i < runs.length; i += 1) {
    if (!runs[i].underline) {
      out += emphasisHtml(runs[i]);
      continue;
    }
    let inner = "";
    while (i < runs.length && runs[i].underline) {
      inner += emphasisHtml(runs[i]);
      i += 1;
    }
    i -= 1;
    out += `<u>${inner}</u>`;
  }
  return out;
};

/**
 * Render lesson markdown as an HTML fragment with inline styles.
 *
 * Inline styles rather than classes because both consumers (the exported PDF
 * and — via the Python mirror — the summary email) render in engines with no
 * stylesheet of ours: email clients strip `<style>` blocks, and the PDF markup
 * is injected as a detached subtree.
 *
 * Every text run is escaped here, so callers pass RAW stored text, never
 * pre-escaped text (escaping twice would show `&amp;` to a parent).
 *
 * @param {string} text - Raw stored lesson text.
 * @param {Object} [styles]
 * @param {string} [styles.paragraph] - Inline style for `<p>`.
 * @param {string} [styles.list] - Inline style for `<ul>`/`<ol>`.
 * @param {string} [styles.item] - Inline style for `<li>`.
 * @returns {string} HTML fragment ("" for empty input).
 */
export function richTextToHtml(text, styles = {}) {
  const attr = (style) => (style ? ` style="${style}"` : "");
  const p = attr(styles.paragraph);
  const list = attr(styles.list);
  const item = attr(styles.item);

  return parseRichText(text)
    .map((block) => {
      if (block.type === "paragraph") {
        return `<p${p}>${block.lines.map(inlineToHtml).join("<br>")}</p>`;
      }
      const tag = block.type === "bullet" ? "ul" : "ol";
      const start = block.type === "ordered" && block.start !== 1 ? ` start="${block.start}"` : "";
      const items = block.lines.map((runs) => `<li${item}>${inlineToHtml(runs)}</li>`).join("");
      return `<${tag}${start}${list}>${items}</${tag}>`;
    })
    .join("");
}

// ---------------------------------------------------------------------------
// WYSIWYG bridge — DOM ⇄ lesson markdown.
//
// The editor is a contenteditable showing real bold/bullets, but what gets
// STORED is still the same plain-text string every other surface renders. So
// these two functions are the whole contract: `richTextToEditorHtml` seeds the
// editable DOM from stored text, `editorDomToRichText` reads it back.
//
// The invariant that matters is a LOSSLESS ROUND TRIP over the subset —
// editorDomToRichText(dom(richTextToEditorHtml(md))) === md. Anything that
// breaks it silently rewrites an instructor's saved notes just because they
// opened the editor, so it is asserted directly over shared fixtures.
// ---------------------------------------------------------------------------

/** Editor-facing HTML: the same structure as `richTextToHtml`, unstyled. */
export function richTextToEditorHtml(text) {
  const html = richTextToHtml(text);
  // A completely empty document still needs one block, or the caret has
  // nowhere to live and the first keystroke creates browser-specific markup.
  return html || "<p><br></p>";
}

const BOLD_TAGS = new Set(["strong", "b"]);
const ITALIC_TAGS = new Set(["em", "i"]);
const UNDERLINE_TAGS = new Set(["u", "ins"]);

/**
 * Flatten an element's inline content into runs carrying the emphasis that is
 * ACTIVE at each point, rather than emitting markers as the walk descends.
 *
 * Emitting on descent is what produced the reported bug: nested elements each
 * wrote their own markers, so `<strong><em>x</em></strong>` became `***x***`
 * and `<b><strong>x</strong></b>` became `****x****` — strings the parser
 * cannot read back, which surfaced to families as stray asterisks around bold
 * text. Emphasis is a SET that is either on or off for a given character; the
 * markers are decided once, from that set.
 */
function collectRuns(node, active, out) {
  node.childNodes.forEach((child) => {
    if (child.nodeType === 3) {
      out.push({ text: child.nodeValue, ...active });
      return;
    }
    if (child.nodeType !== 1) return;
    const tag = child.tagName.toLowerCase();
    if (tag === "br") {
      out.push({ text: "\n", ...active });
      return;
    }
    collectRuns(
      child,
      {
        // Already-active emphasis stays active — it is not re-applied, so a
        // doubly-wrapped element yields one set of markers, not two.
        bold: active.bold || BOLD_TAGS.has(tag),
        italic: active.italic || ITALIC_TAGS.has(tag),
        underline: active.underline || UNDERLINE_TAGS.has(tag),
      },
      out,
    );
  });
  return out;
}

/** Markers for an emphasis set. The subset expresses all three combinations. */
const markerFor = ({ bold, italic }) => (bold && italic ? "***" : bold ? "**" : italic ? "*" : "");

/** Bold and italic markers for runs that share an underline state. */
function emphasisToRichText(runs) {
  let out = "";
  for (let i = 0; i < runs.length; i += 1) {
    // Merge neighbours sharing an emphasis set so "**a****b**" can't happen.
    let text = runs[i].text;
    while (
      i + 1 < runs.length &&
      runs[i + 1].bold === runs[i].bold &&
      runs[i + 1].italic === runs[i].italic
    ) {
      i += 1;
      text += runs[i].text;
    }
    const marker = markerFor(runs[i]);
    // Whitespace-only emphasis would serialize to bare markers that then parse
    // as literal asterisks — emit the spacing without them.
    out += marker && text.trim() ? `${marker}${text}${marker}` : text;
  }
  return out;
}

/**
 * Serialize one element's inline content (text + <strong>/<em>/<u>) to
 * markers. A stretch of underlined runs becomes ONE `++…++` with its bold and
 * italic inside, the shape the parser reads back, rather than a pair of
 * markers per run.
 */
function inlineToRichText(node) {
  const runs = collectRuns(node, { bold: false, italic: false, underline: false }, []);
  let out = "";
  for (let i = 0; i < runs.length; ) {
    const underline = runs[i].underline;
    const group = [];
    while (i < runs.length && runs[i].underline === underline) {
      group.push(runs[i]);
      i += 1;
    }
    const text = emphasisToRichText(group);
    out += underline && text.trim() ? `++${text}++` : text;
  }
  return out;
}

/**
 * Read a contenteditable element back out as lesson markdown.
 *
 * Browsers disagree about what `execCommand` leaves behind — Chrome wraps
 * lines in `<div>`, Firefox and Safari lean on `<p>` and bare text nodes — so
 * this walks the tree by role (block / list / inline) rather than trusting any
 * one engine's markup, and ignores every tag outside the subset.
 *
 * @param {HTMLElement} root
 * @returns {string} Lesson markdown, ready to store.
 */
export function editorDomToRichText(root) {
  if (!root) return "";
  const blocks = [];

  const pushParagraph = (text) => {
    // Trailing whitespace-only lines are an artifact of contenteditable, not
    // content the instructor typed.
    const lines = text.split("\n").map((l) => l.replace(/\u00a0/g, " ").trimEnd());
    while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
    while (lines.length && !lines[0].trim()) lines.shift();
    if (lines.length) blocks.push(lines.join("\n"));
  };

  const walk = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        if (child.nodeValue.trim()) pushParagraph(child.nodeValue);
        return;
      }
      if (child.nodeType !== 1) return;
      const tag = child.tagName.toLowerCase();

      if (tag === "ul" || tag === "ol") {
        const ordered = tag === "ol";
        const items = [];
        child.querySelectorAll(":scope > li").forEach((li, i) => {
          const text = inlineToRichText(li).replace(/\u00a0/g, " ").trim();
          if (text) items.push(ordered ? `${i + 1}. ${text}` : `- ${text}`);
        });
        if (items.length) blocks.push(items.join("\n"));
        return;
      }

      if (tag === "p" || tag === "div") {
        // A nested list inside a block still has to become its own block.
        if (child.querySelector("ul, ol")) {
          walk(child);
          return;
        }
        pushParagraph(inlineToRichText(child));
        return;
      }

      if (tag === "br") {
        return;
      }

      // Anything else (a pasted heading, a stray span) contributes its text.
      pushParagraph(inlineToRichText(child));
    });
  };

  walk(root);
  // Blocks are separated by a blank line — which is exactly how the parser
  // reads a paragraph break back in.
  return blocks.join("\n\n");
}
